import * as Sentry from '@sentry/react';
import { spanStatusfromHttpCode } from '@sentry/tracing';
import { Transaction } from '@sentry/types';
import fetch from 'isomorphic-unfetch';
import { ConfigInterface, mutate } from 'swr';
import { cleanPostBody } from '.';
import { RailsApiResponseFallback } from '../constants';
import RailsApiResponse from '../types/api/RailsApiResponse';
import { RegistrationPostalCodeAutofill } from '../types/api/user/Registration';

const USER_STATUS_ENDPOINT = '/restapi/v3/user/status';

const clientOs = (): string | undefined => {
  if (typeof navigator === 'undefined') return undefined;
  if (navigator.userAgentData?.platform === 'Android') return 'Android';
  if (/Android/i.test(navigator.userAgent)) return 'Android';
  if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) return 'iOS';
  return undefined;
};

const CLIENT_OS = clientOs();

const revalidateUserStatusAfterUnauthorized = (requestUrl: string) => {
  const requestPath = requestUrl.split('?', 1)[0];
  if (!requestPath.endsWith(USER_STATUS_ENDPOINT)) {
    mutate(USER_STATUS_ENDPOINT);
  }
};

export const formatUrl = (
  url: string,
  params: { [key: string]: string | number | undefined } = {},
): string => {
  const paramsArr = Object.keys(params)
    .filter(key => params[key])
    .map(key => `${key}=${params[key]}`);
  return `${url}${paramsArr.length ? '?' : ''}${paramsArr.join('&')}`;
};

export const formatApiUrl = (
  url: string,
  params: { [key: string]: string | number | undefined } = {},
): string => {
  const getUrl = url.startsWith('http')
    ? url
    : `${window.__config__.apiUrl}${url}`;

  const paramsArr = Object.keys(params)
    .filter(key => params[key])
    .map(key => `${key}=${params[key]}`);
  return `${getUrl}${paramsArr.length ? '?' : ''}${paramsArr.join('&')}`;
};

interface GetApiOptions {
  responseText?: boolean;
  cache?: RequestCache;
  sentryScope?: Transaction | null;
}

export const getApi = <T>(
  url: string,
  options: GetApiOptions = {},
): Promise<T> => {
  const config: RequestInit = {
    mode: 'cors',
    credentials: 'include',
    cache: 'no-cache',
    headers: new Headers(),
  };
  if (window.user_locale) {
    (config.headers as Headers).append('content-language', window.user_locale);
  }
  const currentSentryTransaction =
    options.sentryScope || Sentry.getCurrentHub()?.getScope()?.getTransaction();
  const httpSpan = currentSentryTransaction?.startChild({
    op: 'http',
    description: `GET ${url}`,
  });
  const getUrl = formatApiUrl(url);

  return fetch(getUrl, config)
    .then(async res => {
      httpSpan?.setTag('http.statusCode', res.status);
      httpSpan?.setStatus(spanStatusfromHttpCode(res.status));
      if (!res.ok && res.status !== 400) {
        const unauthorized = [401, 403].includes(res.status);
        if (![401, 403].includes(res.status)) {
          Sentry.captureMessage(`Api request failed`, {
            level: Sentry.Severity.Fatal,
            tags: {
              url,
              'http.status': res.status,
            },
          });
        } else {
          revalidateUserStatusAfterUnauthorized(url);
        }
        return Promise.reject<RailsApiResponse<null>>({
          ...RailsApiResponseFallback,
          Code: res.status,
          Unauthorized: unauthorized,
        });
      }
      if (options?.responseText) {
        return res.text();
      }
      const json = await res.json();
      Sentry.addBreadcrumb({
        category: 'api',
        type: 'http',
        data: {
          method: 'GET',
          url: getUrl,
          status_code: res.status,
        },
      });
      httpSpan?.setData('Code', json.Code);
      return json;
    })
    .finally(() => httpSpan?.finish());
};

export interface PostOptions {
  formData?: boolean;
  sentryScope?: Transaction | null;
}

export const postApi = <T>(
  url: string,
  body?: {
    [key: string]: number | string | Blob | boolean | undefined | null | object;
  },
  options: PostOptions = {},
): Promise<T> => {
  const config: RequestInit = {
    method: 'post',
    mode: 'cors',
    credentials: 'include',
    headers: new Headers(),
  };

  if (window.user_locale) {
    (config.headers as Headers).append('content-language', window.user_locale);
  }

  if (CLIENT_OS) {
    (config.headers as Headers).append('x-client-os', CLIENT_OS);
  }

  if (body) {
    if (options.formData) {
      const formData = new FormData();
      const formBody = cleanPostBody(body);
      for (const key in formBody) {
        if (Array.isArray(formBody[key])) {
          for (const item of formBody[key]) {
            formData.append(key, item as string | Blob);
          }
        } else {
          formData.append(key, formBody[key] as string | Blob);
        }
      }
      config.body = formData;
    } else {
      config.body = JSON.stringify(cleanPostBody(body));
      (config.headers as Headers).append('Content-Type', 'application/json');
    }
  }

  const postUrl = url.startsWith('http')
    ? url
    : `${window.__config__.apiUrl}${url}`;

  return fetch(postUrl, config).then(async res => {
    if (!res.ok && res.status !== 400) {
      const unauthorized = [401, 403].includes(res.status);
      if (unauthorized) revalidateUserStatusAfterUnauthorized(url);
      return Promise.reject<RailsApiResponse<null>>({
        ...RailsApiResponseFallback,
        Code: res.status,
        Unauthorized: unauthorized,
      });
    }
    try {
      return await res.json();
    } catch {
      return res;
    }
  });
};

export const SwrFetcherConfig: ConfigInterface<
  any,
  any,
  (...args: any) => any
> = {
  fetcher: getApi,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  errorRetryCount: 2,
  dedupingInterval: 30000,
  focusThrottleInterval: 10000,
  errorRetryInterval: 10000,
  onError: (err, key) => {
    if (![401, 403].includes(err.status)) {
      Sentry.captureMessage(
        `Request failed ${key} with status ${err.status}`,
        Sentry.Severity.Fatal,
      );
    }
  },
};

export const formatSuccesfullRailsApiResponse = <T>(
  data: T,
): RailsApiResponse<T> => ({
  Code: 0,
  Data: data,
  Message: '',
  Success: true,
});

export const API_VALIDATIONS = {
  login: async (
    login: string,
  ): Promise<
    RailsApiResponse<{
      login_exists: boolean;
    }>
  > => {
    const res = await postApi<
      RailsApiResponse<{
        login_exists: boolean;
      }>
    >('/restapi/v1/validators/check_login_exists', {
      login,
    }).catch(err => err);
    return res;
  },
  phone_number: async (phone_number: string): Promise<RailsApiResponse<{}>> => {
    const res = await postApi<RailsApiResponse<{}>>(
      '/restapi/v1/registration/check/phone_number',
      {
        phone_number,
      },
    ).catch(err => err);
    return res;
  },
  email: async (email: string): Promise<RailsApiResponse<{}>> => {
    const res = await postApi<RailsApiResponse<{}>>(
      '/restapi/v1/registration/check/email',
      {
        email,
      },
    ).catch(err => err);
    return res;
  },
  personalCode: async (
    personal_code: string,
  ): Promise<RailsApiResponse<{}>> => {
    const res = await postApi<RailsApiResponse<{}>>(
      '/restapi/v1/registration/check/personal_code',
      {
        personal_code,
      },
    ).catch((err: RailsApiResponse<{}>) => err);
    return res;
  },
  postalCode: async (
    post_code: string,
  ): Promise<RailsApiResponse<RegistrationPostalCodeAutofill | null>> => {
    const res = await postApi<
      RailsApiResponse<RegistrationPostalCodeAutofill | null>
    >('/restapi/v1/registration/check/post_code', {
      post_code,
    }).catch((err: RailsApiResponse<null>) => err);
    return res;
  },
  bonusCode: async (
    bonusCode: string,
  ): Promise<RailsApiResponse<{} | null>> => {
    const requestUri = '/restapi/v1/registration/check/bonus_code';
    const requestBody = { bonusCode };
    const response = await postApi<
      RailsApiResponse<RegistrationPostalCodeAutofill | null>
    >(requestUri, requestBody).catch((err: RailsApiResponse<null>) => err);
    return response;
  },
};
